import { ClerkProvider } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { useTheme } from '../contexts/ThemeContext';
import { useEffect, useState } from 'react';

// Import your Publishable Key
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
    throw new Error('Missing Publishable Key');
}

export const ClerkProviderWithRoutes = ({ children }) => {
    const { theme } = useTheme();
    const [useDark, setUseDark] = useState(() => {
        if (typeof window === 'undefined') return theme === 'dark';
        if (theme === 'dark') return true;
        if (theme === 'light') return false;
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    });

    useEffect(() => {
        if (theme === 'system' && typeof window !== 'undefined' && window.matchMedia) {
            const mql = window.matchMedia('(prefers-color-scheme: dark)');
            const handler = e => setUseDark(e.matches);
            setUseDark(mql.matches);
            if (mql.addEventListener) mql.addEventListener('change', handler);
            else mql.addListener(handler);
            return () => {
                if (mql.removeEventListener) mql.removeEventListener('change', handler);
                else mql.removeListener(handler);
            };
        }
        setUseDark(theme === 'dark');
    }, [theme]);

    // Use the History API so this provider can be mounted outside the RouterProvider.
    const routerPush = to => {
        if (typeof window === 'undefined') return;
        try {
            window.history.pushState({}, '', to);
            window.dispatchEvent(new PopStateEvent('popstate'));
        } catch (e) {
            // Fallback to full navigation
            console.error(e);
            window.location.assign(to);
        }
    };

    const routerReplace = to => {
        if (typeof window === 'undefined') return;
        try {
            window.history.replaceState({}, '', to);
            window.dispatchEvent(new PopStateEvent('popstate'));
        } catch (e) {
            console.error(e);
            window.location.replace(to);
        }
    };

    // Allow overriding the Clerk JS loader URL via env var. If not provided,
    // fall back to a reliable CDN host for the Clerk JS bundle so the app
    // doesn't depend on the account-specific CDN which may be blocked or misconfigured.
    const clerkJsUrl =
        import.meta.env.VITE_CLERK_JS_URL || 'https://cdn.jsdelivr.net/npm/@clerk/clerk-js@5.125.3/dist/clerk.browser.js';

    return (
        <ClerkProvider
            routerPush={routerPush}
            routerReplace={routerReplace}
            signInUrl="/sign-in"
            signUpUrl="/sign-up"
            afterSignOutUrl="/"
            publishableKey={PUBLISHABLE_KEY}
            clerkJSUrl={clerkJsUrl}
            appearance={{
                baseTheme: useDark ? dark : undefined,
            }}
        >
            {children}
        </ClerkProvider>
    );
};
export default ClerkProviderWithRoutes;
